import { useNavigate } from 'react-router-dom';
import { Globe, Smartphone, Puzzle, BookText, Check, ArrowRight, type LucideIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ONBOARDING_LINKS, isDesktopChromium } from '../lib/onboarding-links';

interface SetupCard {
  icon: LucideIcon;
  title: string;
  body: string;
  /** "You're here" when this is the current surface — renders a check, no link. */
  here?: boolean;
  href?: string;
  /** Plain text shown instead of a link (e.g. a short URL to type on another device). */
  hint?: string;
  cta?: string;
}

function buildCards(): SetupCard[] {
  const desktopChrome = isDesktopChromium();
  return [
    {
      icon: Globe,
      title: 'Web app',
      body: 'Read and organize your queue from any browser.',
      here: true,
    },
    {
      icon: Smartphone,
      title: 'Mobile app',
      body: 'Save from your phone and read on the go.',
      href: ONBOARDING_LINKS.mobile,
      cta: 'Get the app',
    },
    {
      icon: Puzzle,
      title: 'Browser extension',
      body: desktopChrome
        ? 'Save the page you’re reading with one click.'
        : 'Save any page with one click — install it on your desktop browser.',
      ...(desktopChrome
        ? { href: ONBOARDING_LINKS.extensionWebStore, cta: 'Add to Chrome' }
        : { hint: ONBOARDING_LINKS.extensionShortUrl }),
    },
    {
      icon: BookText,
      title: 'Obsidian plugin',
      body: 'Sync saved articles into your vault as markdown.',
      href: ONBOARDING_LINKS.obsidianGuide,
      cta: 'Setup guide',
    },
  ];
}

function SetupCardRow({ card }: { card: SetupCard }) {
  const Icon = card.icon;
  return (
    <Card padding="lg" elevation={1} className="flex items-center gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-surface-2">
        <Icon size={20} className="text-prism-violet" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">{card.title}</p>
        <p className="text-xs mt-0.5 text-text-secondary">{card.body}</p>
      </div>
      <div className="flex-shrink-0">
        {card.here ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-prism-emerald">
            <Check size={14} /> You’re here
          </span>
        ) : card.href ? (
          <a
            href={card.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
          >
            {card.cta} <ArrowRight size={14} />
          </a>
        ) : (
          <code className="text-xs text-text-muted">{card.hint}</code>
        )}
      </div>
    </Card>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();

  function handleContinue() {
    navigate('/queue');
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-surface-bg">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text-primary">You’re in 🌱</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Grove works best when it’s everywhere you read. Set up the surfaces you want — you can skip and do this later from Settings.
          </p>
        </div>

        <div className="space-y-3">
          {buildCards().map((card) => (
            <SetupCardRow key={card.title} card={card} />
          ))}
        </div>

        <Button onClick={handleContinue} className="w-full mt-8">
          Continue to Grove
        </Button>
      </div>
    </div>
  );
}
